import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Layout, Spin, message } from 'antd';
import api from '../api';

const ROUTE_PERMISSION_MAP = [
  {
    prefix: '/staff-management',
    permission: 'staff_management_tab',
    label: 'Staff Management',
  },
  {
    prefix: '/add-regular-staff',
    permission: 'staff_management_tab',
    label: 'Staff Management',
  },
  {
    prefix: '/attendance',
    permission: 'attendance_tab',
    label: 'Attendance',
  },
  {
    prefix: '/leave/requests',
    permission: ['leave_requests_tab', 'leave_tab'],
    label: 'Leave Requests',
  },
  {
    prefix: '/leave-requests',
    permission: ['leave_requests_tab', 'leave_tab'],
    label: 'Leave Requests',
  },
  {
    prefix: '/leave/encashment',
    permission: ['leave_encashment_tab', 'leave_tab'],
    label: 'Leave Encashment',
  },
  {
    prefix: '/leave-encashment',
    permission: ['leave_encashment_tab', 'leave_tab'],
    label: 'Leave Encashment',
  },
  {
    prefix: '/leave-templates',
    permission: ['leave_tab', 'settings_tab'],
    label: 'Leave Templates',
  },
  {
    prefix: '/holiday-templates',
    permission: ['leave_tab', 'settings_tab'],
    label: 'Holiday Templates',
  },
  {
    prefix: '/payroll',
    permission: 'payroll_tab',
    label: 'Payroll',
  },
  {
    prefix: '/salary',
    permission: 'payroll_tab',
    label: 'Payroll',
  },
  {
    prefix: '/employee-salary',
    permission: 'payroll_tab',
    label: 'Payroll',
  },
  {
    prefix: '/fnf',
    permission: 'payroll_tab',
    label: 'Payroll',
  },
  {
    prefix: '/payroll-inspection',
    permission: 'payroll_tab',
    label: 'Payroll',
  },
  {
    prefix: '/loans',
    permission: 'loans_tab',
    label: 'Loans',
  },
  {
    prefix: '/advances',
    permission: 'loans_tab',
    label: 'Loans & Advances',
  },
  {
    prefix: '/sales',
    permission: 'sales_tab',
    label: 'Sales',
  },
  {
    prefix: '/reports',
    permission: 'reports_tab',
    label: 'Reports',
  },
  {
    prefix: '/assets-management',
    permission: 'assets_tab',
    label: 'Assets',
  },
  {
    prefix: '/expense-management',
    permission: 'expenses_tab',
    label: 'Expenses',
  },
  {
    prefix: '/geolocation',
    permission: 'geolocation_tab',
    label: 'Geolocation',
  },
  {
    prefix: '/letters',
    permission: 'letters_tab',
    label: 'Letters',
  },
  {
    prefix: '/task-management',
    permission: 'task_management_tab',
    label: 'Task Management',
  },
  {
    prefix: '/roster',
    permission: 'roster_tab',
    label: 'Roster Management',
  },
  {
    prefix: '/ai-reports',
    permission: 'ai_reports_tab',
    label: 'AI Reports',
  },
  {
    prefix: '/performance',
    permission: 'performance_tab',
    label: 'Performance',
  },
  {
    prefix: '/appraisal',
    permission: 'performance_tab',
    label: 'Performance',
  },
  {
    prefix: '/rating-system',
    permission: 'performance_tab',
    label: 'Performance',
  },
  {
    prefix: '/settings',
    permission: 'settings_tab',
    label: 'Settings',
  },
  {
    prefix: '/user-access',
    permission: 'settings_tab',
    label: 'Settings',
  },
  {
    prefix: '/roles-permissions',
    permission: 'settings_tab',
    label: 'Settings',
  },
  {
    prefix: '/business-functions',
    permission: 'settings_tab',
    label: 'Settings',
  },
  {
    prefix: '/shift-settings',
    permission: 'settings_tab',
    label: 'Settings',
  },
  {
    prefix: '/attendance-templates',
    permission: 'settings_tab',
    label: 'Settings',
  },
  {
    prefix: '/weekly-off-templates',
    permission: 'settings_tab',
    label: 'Settings',
  },
  {
    prefix: '/manage-documents',
    permission: 'settings_tab',
    label: 'Settings',
  },
  {
    prefix: '/manage-salary-template',
    permission: 'settings_tab',
    label: 'Settings',
  },
  {
    prefix: '/salary-calculation-logic',
    permission: 'settings_tab',
    label: 'Settings',
  },
  {
    prefix: '/tally-settings',
    permission: 'settings_tab',
    label: 'Settings',
  },
  {
    prefix: '/payout-settings',
    permission: 'settings_tab',
    label: 'Settings',
  },
  {
    prefix: '/dashboard',
    permission: 'dashboard_tab',
    label: 'Dashboard',
  },
];

function getFirstAllowedRoute(permissionKeys) {
  if (permissionKeys.includes('dashboard_tab')) return '/dashboard';
  if (permissionKeys.includes('attendance_tab')) return '/attendance';
  if (permissionKeys.includes('leave_requests_tab') || permissionKeys.includes('leave_tab')) return '/leave/requests';
  if (permissionKeys.includes('payroll_tab')) return '/payroll';
  if (permissionKeys.includes('staff_management_tab')) return '/staff-management';
  if (permissionKeys.includes('loans_tab')) return '/loans';
  if (permissionKeys.includes('sales_tab')) return '/sales';
  if (permissionKeys.includes('expenses_tab')) return '/expense-management';
  if (permissionKeys.includes('task_management_tab')) return '/task-management';
  if (permissionKeys.includes('roster_tab')) return '/roster';
  return '/staff/dashboard';
}

const PrivateRoute = ({ children, allowedRoles }) => {
  const location = useLocation();
  const token = sessionStorage.getItem('impersonate_token') || localStorage.getItem('token');
  const user = JSON.parse(sessionStorage.getItem('impersonate_user') || localStorage.getItem('user') || '{}');

  const [permissions, setPermissions] = useState(() => {
    try {
      const cached = sessionStorage.getItem('sidebar_permission_keys');
      return cached ? JSON.parse(cached) : null;
    } catch (_) {
      return null;
    }
  });

  const isSuperadminPanel = !!user.isSuperadminPanel;
  const perms = typeof user.permissions === 'string' ? JSON.parse(user.permissions) : (user.permissions || {});
  const role = user.role;
  const isStaff = role === 'staff';

  const [loadingPerms, setLoadingPerms] = useState(isStaff && permissions === null);

  useEffect(() => {
    if (isStaff && permissions === null) {
      api.get('/admin/user-access/my-sidebar-permissions')
        .then((res) => {
          const keys = Array.isArray(res?.data?.permissionKeys) ? res.data.permissionKeys : [];
          try {
            sessionStorage.setItem('sidebar_permission_keys', JSON.stringify(keys));
          } catch (_) {}
          setPermissions(keys);
        })
        .catch(() => {
          setPermissions([]);
        })
        .finally(() => {
          setLoadingPerms(false);
        });
    }
  }, [isStaff, permissions]);

  if (!token) {
    return <Navigate to="/" replace />;
  }

  if (allowedRoles) {
    const requiresSuper = allowedRoles.includes('superadmin') || allowedRoles.includes('superadmin_staff');
    const hasAccess = allowedRoles.includes(role) || (isSuperadminPanel && requiresSuper);

    if (!hasAccess) {
      if (role === 'superadmin') return <Navigate to="/superadmin/clients" replace />;
      if (isSuperadminPanel) return <Navigate to="/superadmin/dashboard" replace />;
      if (role === 'channel_partner') return <Navigate to="/partner/clients" replace />;
      return <Navigate to="/dashboard" replace />;
    }
  }

  if (isStaff && loadingPerms) {
    return (
      <Layout style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </Layout>
    );
  }

  // Check route-level tab permissions for staff
  if (isStaff && Array.isArray(permissions)) {
    const rawPath = (location.pathname || '').toLowerCase();
    const normalizedPath = decodeURIComponent(rawPath).replace(/\s+/g, '-');

    // Staff Portal routes are always permitted
    const isStaffPortalRoute = normalizedPath.startsWith('/staff/') && (
      normalizedPath.startsWith('/staff/dashboard') ||
      normalizedPath.startsWith('/staff/attendance') ||
      normalizedPath.startsWith('/staff/leave') ||
      normalizedPath.startsWith('/staff/roster') ||
      normalizedPath.startsWith('/staff/salary') ||
      normalizedPath.startsWith('/staff/advances') ||
      normalizedPath.startsWith('/staff/expenses') ||
      normalizedPath.startsWith('/staff/tasks') ||
      normalizedPath.startsWith('/staff/todo') ||
      normalizedPath.startsWith('/staff/sales') ||
      normalizedPath.startsWith('/staff/profile') ||
      normalizedPath.startsWith('/staff/my-profile')
    );

    if (!isStaffPortalRoute) {
      // Check if accessing staff management via /staff/:id/profile
      const isStaffProfileMgmt = normalizedPath.startsWith('/staff/') && normalizedPath.endsWith('/profile');

      let matchedRule = ROUTE_PERMISSION_MAP.find((r) =>
        normalizedPath === r.prefix || normalizedPath.startsWith(r.prefix + '/') || (isStaffProfileMgmt && r.prefix === '/staff-management')
      );

      // Special check for root path '/'
      if (!matchedRule && normalizedPath === '/') {
        matchedRule = ROUTE_PERMISSION_MAP.find((r) => r.prefix === '/dashboard');
      }

      if (matchedRule) {
        const needed = Array.isArray(matchedRule.permission) ? matchedRule.permission : [matchedRule.permission];
        const hasPerm = needed.some((k) => permissions.includes(k));

        if (!hasPerm) {
          message.warning(`Access denied: You do not have permission to access ${matchedRule.label}`);
          const fallback = getFirstAllowedRoute(permissions);
          return <Navigate to={fallback} replace />;
        }
      }
    }
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {children}
    </Layout>
  );
};

export default PrivateRoute;
